import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { catchError, map, mergeMap, Observable, of, tap } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as licensesActions from '../actions/licenses';
import * as profileActions from '../actions/profile';
import * as fromLicenses from '../reducers/licenses';
import {
  LicenseItem,
  LicensesQueryParams,
  LicensesResponse,
  LicensesService,
  ValidateOrActivateLicenseKeysBody,
} from '../standalone-core/pages/licenses';
import { ApiPaginateResponse } from '../models/api/api-response';
import { LicenseService } from '../services/license.service';
import * as fromProfile from '../reducers/profile';
import { HttpResponse } from '@angular/common/http';
import { aggregateInvalidLicenseErrors } from '@app/standalone-core';
import { ApplicationStore } from 'app/state-management/application/application.store';

@Injectable()
export class LicensesEffects {
  private readonly licensesService: LicensesService = inject(LicensesService);
  private readonly licenseService: LicenseService = inject(LicenseService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly store: Store = inject(Store);
  private readonly appStore: ApplicationStore = inject(ApplicationStore);
  private actions$: Actions = inject(Actions);

  loadLicenses$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.loadLicenses),
      concatLatestFrom(() => [
        this.store.select(fromLicenses.selectPage),
        this.store.select(fromLicenses.selectCount),
        this.store.select(fromLicenses.selectOrder),
        this.store.select(fromProfile.selectIsLicenseModeActive),
      ]),
      filter(([, , , , isLicenseModeActive]) => isLicenseModeActive),
      switchMap(([, page, count, order]) => {
        const offset: number = Math.max((page - 1) * count, 0);
        const params: LicensesQueryParams = {
          count,
          offset,
          orderBy: order != null ? (order.sortField ?? order.colId) : null,
          orderDirection: order != null ? (order.ascending ? 'asc' : 'desc') : null,
        };
        return this.licensesService.getLicenses(params).pipe(
          map((response: ApiPaginateResponse<LicenseItem>) => {
            const result: LicenseItem[] = response?.items;
            const totalPages: number = response?.total && count > 0 ? Math.ceil(response.total / count) : 0;
            return licensesActions.loadLicensesSuccess({ licenses: result ?? [], totalPages });
          }),
          catchError(error => of(licensesActions.loadLicensesFail({ error })))
        );
      })
    );
  });

  changeLicensesPage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.changeLicensesPage),
      map(() => licensesActions.loadLicenses()),
    );
  });

  changeLicensesPageSize$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.changeLicensesPageSize),
      map(() => licensesActions.loadLicenses()),
    );
  });

  changeLicensesOrder$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.changeLicensesOrder),
      map(() => licensesActions.loadLicenses()),
    );
  });

  validateLicenses$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.validateLicenses),
      switchMap(({ licenseKeys, licenseAction }) => this.licensesService.validateLicenses(licenseKeys, licenseAction).pipe(
        map((response: LicensesResponse) => licensesActions.validateLicensesSuccess({ validLicenses: response?.licenses || [] })),
        catchError(error => of(licensesActions.validateLicensesFailure({ error })))
      ))
    );
  });

  activateLicenses$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.activateLicenses),
      switchMap(({ licenses }) => {
        const body: ValidateOrActivateLicenseKeysBody[] = licenses.map((item: LicenseItem) => ({
          licenseKey: item?.licenseKey,
          licenseIds: [item?.info?.wibu?.licenseId],
        }));

        return this.licensesService.activateLicenses(body).pipe(
          mergeMap((response: HttpResponse<LicensesResponse>) => {
            const errorMsg: string = aggregateInvalidLicenseErrors(response.body);
            if (response?.status === 207 || errorMsg) {
              return [licensesActions.activateLicensesFailure({ error: errorMsg })];
            }
            return [
              licensesActions.activateLicensesSuccess({ count: licenses.length }),
              licensesActions.loadLicenses(),
            ];
          }),
          catchError(error => of(licensesActions.activateLicensesFailure({ error })))
        );
      })
    );
  });

  activateLicensesSuccess$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.activateLicensesSuccess),
      tap(({ count }) => {
        const text: string = count === 1
          ? this.translateService.instant('LICENSES.ONE_LICENSE_ACTIVATED')
          : this.translateService.instant('LICENSES.LICENSES_ARE_ACTIVATED', { 0: count });
        this.appStore.showSnack({ text, type: 'info', variant: 'a', disappearAfter: 3000, showClose: true, closeOnAction: true });
      })
    );
  }, { dispatch: false });

  reloadLicenseAfterActivation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.activateLicensesSuccess),
      mergeMap(() => {
        this.appStore.clearLicense();
        return of(profileActions.loadIdentity());
      })
    );
  });

  activateLicensesFailure$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.activateLicensesFailure),
      tap(({ error }) => {
        const text: string = typeof error === 'string' ? error : this.translateService.instant('LICENSES.LICENSE_ACTIVATION_FAIL');
        this.appStore.showSnack({ text, type: 'error', variant: 'a', disappearAfter: 3000, showClose: true, closeOnAction: true });
      })
    );
  }, { dispatch: false });

  upgradeLicenses$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.upgradeLicenses),
      switchMap(({ licenses }) => {
        const licenseKeys: string[] = licenses.map((item: LicenseItem) => item?.licenseKey);
        return this.licensesService.upgradeLicenses({ licenseKeys }).pipe(
          mergeMap((response: HttpResponse<LicensesResponse>) => {
            const errorMsg: string = aggregateInvalidLicenseErrors(response.body);
            if (response?.status === 207 || errorMsg) {
              return [licensesActions.upgradeLicensesFailure({ error: errorMsg })];
            }
            return [
              licensesActions.upgradeLicensesSuccess({ count: licenses.length }),
              licensesActions.loadLicenses(),
            ];
          }),
          catchError(error => of(licensesActions.upgradeLicensesFailure({ error })))
        );
      })
    );
  });

  upgradeLicensesSuccess$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.upgradeLicensesSuccess),
      tap(({ count }) => {
        const text: string = count === 1
          ? this.translateService.instant('LICENSES.ONE_LICENSE_UPGRADED')
          : this.translateService.instant('LICENSES.LICENSES_ARE_UPGRADED', { 0: count });
        this.appStore.showSnack({ text, type: 'info', variant: 'a', disappearAfter: 3000, showClose: true, closeOnAction: true });
      })
    );
  }, { dispatch: false });

  reloadLicenseAfterUpgrade$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.upgradeLicensesSuccess),
      mergeMap(() => {
        this.appStore.clearLicense();
        return of(profileActions.loadIdentity());
      })
    );
  });

  upgradeLicensesFailure$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.upgradeLicensesFailure),
      tap(({ error }) => {
        const text: string = typeof error === 'string' ? error : this.translateService.instant('LICENSES.LICENSE_UPGRADE_FAIL');
        this.appStore.showSnack({ text, type: 'error', variant: 'a', disappearAfter: 3000, showClose: true, closeOnAction: true });
      })
    );
  }, { dispatch: false });

  deactivateLicense$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.deactivateLicense),
      switchMap(({ license }) => {
        const body: ValidateOrActivateLicenseKeysBody[] = [{
          licenseKey: license?.licenseKey,
          licenseIds: [license?.info?.wibu?.licenseId],
        }];

        return this.licensesService.deactivateLicenses(body).pipe(
          mergeMap(() => [
            licensesActions.deactivateLicenseSuccess(),
            licensesActions.loadLicenses(),
          ]),
          catchError(error => of(licensesActions.deactivateLicenseFailure({ error })))
        );
      })
    );
  });

  deactivateLicenseSuccess$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.deactivateLicenseSuccess),
      tap(() => {
        const text: string = this.translateService.instant('LICENSES.LICENSE_DEACTIVATED');
        this.appStore.showSnack({ text, type: 'info', variant: 'a', disappearAfter: 3000, showClose: true, closeOnAction: true });
      })
    );
  }, { dispatch: false });

  deactivateLicenseFailure$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(licensesActions.deactivateLicenseFailure),
      tap(() => {
        const text: string = this.translateService.instant('LICENSES.LICENSE_DEACTIVATION_FAIL');
        this.appStore.showSnack({ text, type: 'error', variant: 'a', disappearAfter: 3000, showClose: true, closeOnAction: true });
      })
    );
  }, { dispatch: false });
}
